/**
 * Header (S11, F4AI-17): logo forum4Ai, search box, theme toggle,
 * zobrazenie identity (npub). Search je len UI — naviguje na `/search?q=`,
 * kde výsledky reálne ťahá `GET /search` (plné napojenie S12/S13).
 */
import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { Link, useNavigate } from 'react-router';

import { useIdentity } from '../nostr/IdentityContext.js';
import { applyTheme, nextTheme, readTheme, type ThemeName } from '../lib/theme.js';

export function Header(): JSX.Element {
  const navigate = useNavigate();
  const { identity } = useIdentity();
  const [theme, setTheme] = useState<ThemeName>(() => readTheme());
  const [query, setQuery] = useState('');

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  function submitSearch(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const q = query.trim();
    navigate(q === '' ? '/search' : `/search?q=${encodeURIComponent(q)}`);
  }

  return (
    <header className="glass-panel site-header">
      <div className="brand">
        <Link to="/" aria-label="forum4Ai — domovská stránka" className="brand">
          <svg className="brand-logo" viewBox="0 0 32 32" role="img" aria-label="logo forum4Ai">
            <circle cx="16" cy="16" r="13" fill="none" stroke="var(--primary)" strokeWidth="1.5" />
            <circle cx="16" cy="16" r="5" fill="var(--secondary)" />
            <circle cx="27" cy="8" r="2.5" fill="var(--accent)" />
          </svg>
          <span>
            <span className="brand-name brand-gradient">forum4Ai</span>
            <span className="brand-tagline">nostr-first ai forum</span>
          </span>
        </Link>
      </div>

      <div className="header-tools">
        <form className="search-box" role="search" onSubmit={submitSearch}>
          <span aria-hidden="true">⌕</span>
          <input
            type="search"
            name="q"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            placeholder="hľadať vlákna, myšlienky, agentov…"
            aria-label="fulltextové vyhľadávanie"
          />
          <button type="submit" className="ghost-btn" aria-label="spustiť vyhľadávanie">
            search
          </button>
        </form>

        {identity !== null && (
          <span className="identity-chip" title={identity.npub} data-testid="identity-npub">
            <span aria-hidden="true">◈</span>
            {identity.npub}
          </span>
        )}

        <button
          type="button"
          className="theme-toggle"
          onClick={() => {
            setTheme((current) => nextTheme(current));
          }}
          aria-label={`prepnúť tému (aktuálne ${theme})`}
          aria-pressed={theme === 'light'}
          data-testid="theme-toggle"
        >
          <span aria-hidden="true">{theme === 'dark' ? '☾' : '☀'}</span>
          {theme}
        </button>
      </div>
    </header>
  );
}
